import { MarkerType } from '@xyflow/react';
import { qualifyWorkflowResource } from './qualify-workflow-resource';
import type {
    AslDefinition,
    AslState,
    AslStateType,
    AslChoice,
    ConversionResult,
    WorkflowNode,
    WorkflowEdge,
    WorkflowNodeData,
    WorkflowEdgeData,
    StateNodeData,
    ParallelGatewayNodeData,
    MergeNodeData,
    StartMarkerNodeData,
    MapContainerNodeData,
    ValidationIssue,
    CapabilityType,
    ParallelBranchInfo,
} from '@/types/workflow-graph';
import { extractBadges } from '../StateBadges';

/**
 * Resource registry for looking up resource types.
 * This allows the converter to determine the correct type for resources
 * by looking them up in the available capabilities.
 * 
 * Each resource can optionally have an alias for backward compatibility
 * or alternative naming conventions.
 */
/** Unified shape for all capability types in the resource registry. */
export interface RegistryCapability {
    /** Canonical id (FQCN) — e.g. "more0ai/workflow.capability-factory" */
    name: string;
    /** Short name alias for backward-compatible matching */
    alias?: string;
    aliases?: string[];
    app_key?: string;
}

export interface ResourceRegistry {
    agents: RegistryCapability[];
    tools: RegistryCapability[];
    models: RegistryCapability[];
    workflows: RegistryCapability[];
    /** Fallback app_key for capabilities matched in registry but missing their own app_key (legacy data). */
    fallbackAppKey?: string;
}

/**
 * Check if a resource matches by name or alias.
 * @param resource - The resource object with name and optional alias
 * @param searchValue - The value to match against
 * @returns true if name or alias matches
 */
function matchesNameOrAlias<T extends { name: string; alias?: string; aliases?: string[] }>(
    resource: T,
    searchValue: string
): boolean {
    if (resource.name === searchValue || resource.alias === searchValue) {
        return true;
    }
    if (resource.aliases?.includes(searchValue)) {
        return true;
    }
    const nameSuffix = resource.name.includes('/')
        ? resource.name.slice(resource.name.indexOf('/') + 1)
        : resource.name;
    return nameSuffix === searchValue;
}

function resourceLookupIds(capabilityId: string, fallbackAppKey?: string): string[] {
    const ids = [capabilityId];
    if (fallbackAppKey) {
        const qualified = qualifyWorkflowResource(capabilityId, fallbackAppKey);
        if (qualified !== capabilityId && !ids.includes(qualified)) {
            ids.push(qualified);
        }
    }
    return ids;
}


/**
 * Result of resource type extraction, including validation status.
 */
interface ResourceTypeResult {
    /** The capability type if found */
    type?: CapabilityType;
    /** App key resolved from registry (when available) */
    appKey?: string;
    /** True when the type was confirmed via registry lookup (vs inferred from naming convention) */
    registryMatch?: boolean;
    /** Error message if resource validation failed */
    error?: string;
}

/**
 * Try to infer the capability type from a resource name's dotted prefix.
 * 
 * Naming conventions:
 *   "agent.doc.splitter"  → agent
 *   "tool.doc.extract"    → tool
 *   "model.gpt4o"         → model
 *   "workflow.ingest.v1"  → workflow
 *   "more0ai/workflow.x"  → workflow (FQCN: type is the first segment after app/)
 *   "process-case-file"   → undefined (bare name, workflow-local handler)
 */
function inferTypeFromName(resourceName: string): CapabilityType | undefined {
    const name = resourceName.includes('/')
        ? resourceName.slice(resourceName.indexOf('/') + 1)
        : resourceName;
    const prefixMatch = name.match(/^(agent|tool|model|workflow)\./);
    return prefixMatch?.[1] as CapabilityType | undefined;
}

/**
 * Extract resource type from a Resource string via registry lookup.
 * 
 * Priority:
 * 1. Explicit ResourceType property on the state (if present)
 * 2. Lookup in resource registry by name OR alias (if provided)
 * 3. Explicit type prefix in Resource string (e.g. "agent:app/key")
 * 4. Inferred type from naming convention (e.g. "agent.doc.splitter" → agent)
 * 
 * Resources that cannot be resolved are NOT treated as errors — they may be
 * workflow-local handlers, older data without app_key mappings, or cross-app
 * references not present in the current registry.
 */
function extractResourceType(state: AslState, registry?: ResourceRegistry): ResourceTypeResult {
    // Check for explicit ResourceType property first (if extended in ASL)
    const explicitType = (state as AslState & { ResourceType?: CapabilityType }).ResourceType;
    if (explicitType) {
        return { type: explicitType };
    }
    
    // Get the resource - could be static (Resource) or dynamic (Resource.$)
    const resource = state.Resource;
    const dynamicResource = state['Resource.$'] as string | undefined;
    
    // For dynamic references, we cannot validate at design time
    if (dynamicResource) {
        return {}; // No error - dynamic references are resolved at runtime
    }
    
    // No resource specified - valid for some states, no error
    if (!resource) {
        return {};
    }

    // Extract explicit type prefix from ASL Resource, e.g. "tool:app/key"
    const resourcePrefixMatch = resource.match(/^(agent|tool|model|workflow):/);
    const explicitResourceType = resourcePrefixMatch?.[1] as CapabilityType | undefined;

    // Strip type prefix — ASL stores "agent:app/key", registry entries use capability id "app/key"
    const capabilityId = resource.replace(/^(agent|tool|model|workflow):/, '');
    
    // Look up in registry (if provided), checking both name and alias.
    // Legacy capabilities may exist in the registry without an app_key;
    // fall back to the owning app's key when that happens.
    if (registry) {
        const fallback = registry.fallbackAppKey;
        const lookupIds = resourceLookupIds(capabilityId, fallback);

        for (const lookupId of lookupIds) {
            // Check agents (by name, alias, or FQCN suffix)
            const matchedAgent = registry.agents.find(a => matchesNameOrAlias(a, lookupId));
            if (matchedAgent) {
                return { type: 'agent', appKey: matchedAgent.app_key || fallback, registryMatch: true };
            }
            // Check tools (by name, alias, or FQCN suffix)
            const matchedTool = registry.tools.find(t => matchesNameOrAlias(t, lookupId));
            if (matchedTool) {
                return { type: 'tool', appKey: matchedTool.app_key || fallback, registryMatch: true };
            }
            // Check models (by name, alias, or FQCN suffix)
            const matchedModel = registry.models.find(m => matchesNameOrAlias(m, lookupId));
            if (matchedModel) {
                return { type: 'model', appKey: matchedModel.app_key || fallback, registryMatch: true };
            }
            // Check workflows (same matchesNameOrAlias as agents/tools/models)
            const matchedWorkflow = registry.workflows.find(w => matchesNameOrAlias(w, lookupId));
            if (matchedWorkflow) {
                return {
                    type: 'workflow',
                    appKey: matchedWorkflow.app_key || fallback,
                    registryMatch: true,
                };
            }
        }
    }
    
    // Not found in registry — fall back to explicit prefix or naming convention.
    // These are not errors: could be workflow-local handlers, older data without
    // app_key mappings, or cross-app references outside the current registry.
    if (explicitResourceType) {
        return { type: explicitResourceType };
    }

    const inferredType = inferTypeFromName(capabilityId);
    if (inferredType) {
        return { type: inferredType };
    }

    // Bare name with no inferrable type (e.g. "process-case-file") —
    // likely a workflow-local handler resolved by the engine at runtime.
    return {};
}

// Node dimensions must match the actual rendered WorkflowCard component
// WorkflowCard is w-72 = 288px, min-h-32 = 128px
// These are used for layout calculation BEFORE React Flow measures the nodes
export const NODE_WIDTH = 288;
export const NODE_HEIGHT = 128;

// Gateway node dimensions (fork/join bars) - smaller than regular nodes
// These are thin bars, but we allocate reasonable space for layout
const GATEWAY_NODE_WIDTH = 40;
const GATEWAY_NODE_HEIGHT = 80;

// Start marker dimensions - pill-shaped button with icon and "Start" text
// Actual component has px-3 py-2 padding, icon (h-6 w-6), gap-2, and text
const START_MARKER_WIDTH = 100;
const START_MARKER_HEIGHT = 40;

const NODE_SPACING_X = 300;
const NODE_SPACING_Y = 150;

/**
 * Convert ASL workflow definition to ReactFlow nodes and edges.
 * 
 * This converter follows proper ASL semantics:
 * - Parallel states are visualized with explicit fork/join bars (AND semantics)
 * - Choice states maintain their diamond visualization
 * - Map states show their internal iterator structure
 * - Catch edges are styled distinctly from Next edges
 * - StartMarker node is added for visual clarity
 * 
 * @param definition - The ASL workflow definition to convert
 * @param parentId - Optional parent node ID for nested workflows
 * @param offsetX - X position offset
 * @param offsetY - Y position offset
 * @param registry - Optional resource registry for looking up resource types
 */
export function convertAslToReactFlow(
    definition: AslDefinition,
    parentId?: string,
    offsetX = 0,
    offsetY = 0,
    registry?: ResourceRegistry
): ConversionResult {
    const nodes: WorkflowNode[] = [];
    const edges: WorkflowEdge[] = [];
    const validationIssues: ValidationIssue[] = [];

    const states = definition.States || {};
    const startAt = definition.StartAt;

    // Track which states are Parallel states (for edge redirection)
    const parallelStates = new Set<string>();
    for (const [stateName, state] of Object.entries(states)) {
        if (state.Type === 'Parallel' && state.Branches && state.Branches.length > 0) {
            parallelStates.add(stateName);
        }
    }

    // Track visited states for layout
    const visited = new Set<string>();
    const positions = new Map<string, { x: number; y: number }>();

    // Calculate positions using topological sort / BFS
    calculatePositions(states, startAt, positions, visited, offsetX, offsetY);

    // Reset visited for node creation
    visited.clear();

    // Add StartMarker node (only at root level)
    if (!parentId) {
        const startMarkerData: StartMarkerNodeData = {
            nodeType: 'startMarker',
            label: 'Start',
            targetStateName: startAt,
        };

        const startPosition = positions.get(startAt) || { x: 0, y: 0 };
        nodes.push({
            id: '__start__',
            type: 'startMarker',
            position: { x: startPosition.x - 100, y: startPosition.y },
            data: startMarkerData,
            // Set dimensions for layout calculation
            width: START_MARKER_WIDTH,
            height: START_MARKER_HEIGHT,
        });

        // Connect StartMarker to StartAt state only when there is a target state
        if (startAt && states[startAt]) {
            let startTargetId = startAt;
            if (parallelStates.has(startAt)) {
                startTargetId = `${startAt}-fork`;
            }

            const startEdgeData: WorkflowEdgeData = {
                edgeType: 'normal',
            };
            edges.push({
                id: `__start__->${startTargetId}`,
                source: '__start__',
                sourceHandle: 'source',
                target: startTargetId,
                targetHandle: 'target',
                type: 'workflow',
                data: startEdgeData,
                markerEnd: { type: MarkerType.ArrowClosed, color: '#374151' },
            });
        }
    }

    // Create nodes and handle special cases
    for (const [stateName, state] of Object.entries(states)) {
        const position = positions.get(stateName) || { x: 0, y: 0 };
        const nodeId = parentId ? `${parentId}-${stateName}` : stateName;

        // Handle Parallel state - create fork/join structure
        if (state.Type === 'Parallel' && state.Branches && state.Branches.length > 0) {
            const parallelResult = createParallelStructure(
                nodeId,
                stateName,
                state,
                position,
                parentId,
                registry
            );
            nodes.push(...parallelResult.nodes);
            edges.push(...parallelResult.edges);
            if (parallelResult.validationIssues) {
                validationIssues.push(...parallelResult.validationIssues);
            }

            // If the Parallel state has a Next, connect join to it
            if (state.Next) {
                let targetId = parentId ? `${parentId}-${state.Next}` : state.Next;
                // If Next is also a Parallel state, redirect to its fork
                if (parallelStates.has(state.Next)) {
                    targetId = `${targetId}-fork`;
                }
                const nextEdgeData: WorkflowEdgeData = {
                    edgeType: 'normal',
                };
                edges.push({
                    id: `${nodeId}-join->${targetId}`,
                    source: `${nodeId}-join`,
                    sourceHandle: 'source',
                    target: targetId,
                    targetHandle: 'target',
                    type: 'workflow',
                    data: nextEdgeData,
                    markerEnd: { type: MarkerType.ArrowClosed, color: '#374151' },
                });
            }

            continue;
        }

        // Handle Map state with Iterator - create container with nested child nodes
        if (state.Type === 'Map' && state.Iterator) {
            const mapResult = createMapContainerStructure(
                nodeId,
                stateName,
                state,
                position,
                startAt,
                parentId,
                registry
            );
            nodes.push(...mapResult.nodes);
            edges.push(...mapResult.edges);
            if (mapResult.validationIssues) {
                validationIssues.push(...mapResult.validationIssues);
            }

            // If the Map state has a Next, connect container to it
            if (state.Next) {
                let targetId = parentId ? `${parentId}-${state.Next}` : state.Next;
                // If Next is also a Parallel state, redirect to its fork
                if (parallelStates.has(state.Next)) {
                    targetId = `${targetId}-fork`;
                }
                const nextEdgeData: WorkflowEdgeData = {
                    edgeType: 'normal',
                };
                edges.push({
                    id: `${nodeId}->${targetId}`,
                    source: nodeId,
                    sourceHandle: 'source',
                    target: targetId,
                    targetHandle: 'target',
                    type: 'workflow',
                    data: nextEdgeData,
                    markerEnd: { type: MarkerType.ArrowClosed, color: '#374151' },
                });
            }

            continue;
        }

        // Standard state node creation
        // Extract resource type for Task states (using registry for lookup)
        const resourceResult = state.Type === 'Task' ? extractResourceType(state, registry) : undefined;
        
        // For nodes in branches (when parentId is set), don't mark as isStart/isEnd
        // because they still need input handles (from fork) and output handles (to join)
        // Only root-level nodes should have these flags set
        const isTerminalState = state.End === true || state.Type === 'Succeed' || state.Type === 'Fail';
        const isStartState = stateName === startAt;
        const isRootLevel = !parentId;
        
        const nodeData: StateNodeData = {
            nodeType: 'state',
            label: stateName,
            stateType: state.Type as AslStateType,
            state,
            isStart: isStartState && isRootLevel, // Only mark isStart for root-level start state
            isEnd: isTerminalState && isRootLevel, // Only mark isEnd for root-level terminal states
            badges: extractBadges(state),
            ...(resourceResult?.type && { resourceType: resourceResult.type }),
            ...(resourceResult?.appKey && { resourceAppKey: resourceResult.appKey }),
            ...(resourceResult?.registryMatch != null && { resourceRegistryMatch: resourceResult.registryMatch }),
            ...(resourceResult?.error && { resourceError: resourceResult.error }),
        };

        nodes.push({
            id: nodeId,
            type: state.Type.toLowerCase(),
            position,
            data: nodeData,
            // Set explicit dimensions for layout calculation before React Flow measures
            width: NODE_WIDTH,
            height: NODE_HEIGHT,
        });

        // Create edges (with redirection for Parallel targets)
        edges.push(...createEdgesForState(nodeId, stateName, state, parentId, parallelStates));
    }

    return { nodes, edges, validationIssues };
}

/**
 * Create the fork/join structure for a Parallel state.
 * 
 * CRITICAL ASL SEMANTICS:
 * - Parallel executes ALL branches concurrently
 * - Execution waits at the join until ALL branches complete
 * - This is TRUE synchronization (AND-join)
 */
function createParallelStructure(
    nodeId: string,
    stateName: string,
    state: AslState,
    position: { x: number; y: number },
    parentId?: string,
    registry?: ResourceRegistry
): ConversionResult {
    const nodes: WorkflowNode[] = [];
    const edges: WorkflowEdge[] = [];
    const branches = state.Branches || [];
    const branchCount = branches.length;

    // Calculate the vertical span needed for all branches
    const totalBranchHeight = branchCount * NODE_SPACING_Y;
    const startY = position.y - totalBranchHeight / 2 + NODE_SPACING_Y / 2;

    // Create fork node (parallelFork type)
    const forkId = `${nodeId}-fork`;
    const forkData: ParallelGatewayNodeData = {
        nodeType: 'parallelFork',
        label: `${stateName} (fork)`,
        branchCount,
        ownerStateName: stateName,
        pairedNodeId: `${nodeId}-join`,
        state,
    };

    nodes.push({
        id: forkId,
        type: 'parallelFork',
        position: { x: position.x, y: position.y },
        data: forkData,
        // Set dimensions for layout calculation
        width: GATEWAY_NODE_WIDTH,
        height: GATEWAY_NODE_HEIGHT,
    });

    // Create join node (parallelJoin type)
    const joinId = `${nodeId}-join`;
    const joinData: ParallelGatewayNodeData = {
        nodeType: 'parallelJoin',
        label: `${stateName} (join)`,
        branchCount,
        ownerStateName: stateName,
        pairedNodeId: forkId,
        state,
        isEnd: state.End === true,
    };

    // Calculate join position (after all branches)
    let maxBranchX = position.x + NODE_SPACING_X * 2;

    // Process each branch
    branches.forEach((branch, branchIdx) => {
        const branchY = startY + branchIdx * NODE_SPACING_Y;
        const branchPrefix = `${nodeId}-branch-${branchIdx}`;

        // Convert branch states
        const branchResult = convertAslToReactFlow(
            branch,
            branchPrefix,
            position.x + NODE_SPACING_X,
            branchY,
            registry
        );

        // Add parentParallelInfo to all state nodes in this branch for replay checkpoint lookup
        const parallelInfo: ParallelBranchInfo = {
            parallelNodeId: stateName,  // Use the Parallel state name as the node ID
            branchIndex: branchIdx,
        };
        
        for (const node of branchResult.nodes) {
            const nodeData = node.data as WorkflowNodeData;
            if (nodeData.nodeType === 'state') {
                (nodeData as StateNodeData).parentParallelInfo = parallelInfo;
            }
        }

        nodes.push(...branchResult.nodes);
        edges.push(...branchResult.edges);

        // Find branch start node (StartAt)
        const branchStartId = `${branchPrefix}-${branch.StartAt}`;

        // Connect fork to branch start with parallel edge type
        const forkEdgeData: WorkflowEdgeData = {
            edgeType: 'parallel',
        };
        edges.push({
            id: `${forkId}-branch-${branchIdx}->${branchStartId}`,
            source: forkId,
            sourceHandle: `branch-${branchIdx}`,
            target: branchStartId,
            targetHandle: 'target',
            type: 'workflow',
            data: forkEdgeData,
            markerEnd: { type: MarkerType.ArrowClosed, color: '#111827' },
        });

        // Find branch end nodes (states with End=true or Succeed/Fail types)
        const branchEndNodes = findBranchEndNodes(branch, branchPrefix);

        // Calculate max X from branch nodes for join positioning
        branchResult.nodes.forEach((node) => {
            if (node.position.x > maxBranchX - NODE_SPACING_X) {
                maxBranchX = node.position.x + NODE_SPACING_X;
            }
        });

        // Connect branch end nodes to join with parallel edge type
        branchEndNodes.forEach((endNodeId) => {
            const joinEdgeData: WorkflowEdgeData = {
                edgeType: 'parallel',
            };
            edges.push({
                id: `${endNodeId}->${joinId}-branch-${branchIdx}`,
                source: endNodeId,
                sourceHandle: 'source',
                target: joinId,
                targetHandle: `branch-${branchIdx}`,
                type: 'workflow',
                data: joinEdgeData,
                markerEnd: { type: MarkerType.ArrowClosed, color: '#111827' },
            });
        });
    });

    // Position join node after all branches
    nodes.push({
        id: joinId,
        type: 'parallelJoin',
        position: { x: maxBranchX, y: position.y },
        data: joinData,
        // Set dimensions for layout calculation
        width: GATEWAY_NODE_WIDTH,
        height: GATEWAY_NODE_HEIGHT,
    });

    return { nodes, edges };
}

/**
 * Create a fork/join structure for a Parallel state that is a child of a container.
 * All created nodes will be parented to the container node.
 */
function createParallelStructureAsChild(
    nodeId: string,
    stateName: string,
    state: AslState,
    position: { x: number; y: number },
    containerId: string,
    parentPrefix: string,
    registry?: ResourceRegistry
): ConversionResult {
    const nodes: WorkflowNode[] = [];
    const edges: WorkflowEdge[] = [];
    const branches = state.Branches || [];
    const branchCount = branches.length;

    // Calculate the vertical span needed for all branches
    const totalBranchHeight = branchCount * NODE_SPACING_Y;
    const startY = position.y - totalBranchHeight / 2 + NODE_SPACING_Y / 2;

    // Create fork node (parallelFork type) as child of container
    const forkId = `${nodeId}-fork`;
    const forkData: ParallelGatewayNodeData = {
        nodeType: 'parallelFork',
        label: `${stateName} (fork)`,
        branchCount,
        ownerStateName: stateName,
        pairedNodeId: `${nodeId}-join`,
        state,
    };

    nodes.push({
        id: forkId,
        type: 'parallelFork',
        position: { x: position.x, y: position.y },
        data: forkData,
        // Set dimensions for layout calculation
        width: GATEWAY_NODE_WIDTH,
        height: GATEWAY_NODE_HEIGHT,
        parentId: containerId,
        extent: 'parent',
        expandParent: true,
        zIndex: 1,
    } as WorkflowNode);

    // Create join node (parallelJoin type) as child of container
    const joinId = `${nodeId}-join`;
    const joinData: ParallelGatewayNodeData = {
        nodeType: 'parallelJoin',
        label: `${stateName} (join)`,
        branchCount,
        ownerStateName: stateName,
        pairedNodeId: forkId,
        state,
        isEnd: state.End === true,
    };

    // Calculate join position (after all branches)
    let maxBranchX = position.x + NODE_SPACING_X * 2;

    // Process each branch
    branches.forEach((branch, branchIdx) => {
        const branchY = startY + branchIdx * NODE_SPACING_Y;
        const branchPrefix = `${nodeId}-branch-${branchIdx}`;
        
        // Create parallel branch info for replay checkpoint lookup
        const parallelInfo: ParallelBranchInfo = {
            parallelNodeId: stateName,  // Use the Parallel state name as the node ID
            branchIndex: branchIdx,
        };

        // For branches inside a container's Parallel, we need to create 
        // the branch nodes as children of the container too
        for (const [branchStateName, branchState] of Object.entries(branch.States || {})) {
            const branchNodeId = `${branchPrefix}-${branchStateName}`;
            const branchNodeX = position.x + NODE_SPACING_X;
            const branchNodeY = branchY;

            // Extract resource type for Task states (using registry for lookup)
            const resourceResult = branchState.Type === 'Task' ? extractResourceType(branchState, registry) : undefined;

            const branchNodeData: StateNodeData = {
                nodeType: 'state',
                label: branchStateName,
                stateType: branchState.Type as AslStateType,
                state: branchState,
                isStart: false,
                isEnd: false,
                badges: extractBadges(branchState),
                parentParallelInfo: parallelInfo,  // Add parallel branch info for replay
                ...(resourceResult?.type && { resourceType: resourceResult.type }),
                ...(resourceResult?.appKey && { resourceAppKey: resourceResult.appKey }),
                ...(resourceResult?.registryMatch != null && { resourceRegistryMatch: resourceResult.registryMatch }),
                ...(resourceResult?.error && { resourceError: resourceResult.error }),
            };

            nodes.push({
                id: branchNodeId,
                type: branchState.Type.toLowerCase(),
                position: { x: branchNodeX, y: branchNodeY },
                data: branchNodeData,
                // Set explicit dimensions for layout calculation before React Flow measures
                width: NODE_WIDTH,
                height: NODE_HEIGHT,
                parentId: containerId,
                extent: 'parent',
                expandParent: true,
                zIndex: 1,
            } as WorkflowNode);

            // Create edges within branch
            edges.push(...createEdgesForState(
                branchNodeId,
                branchStateName,
                branchState,
                branchPrefix,
                new Set()
            ));

            // Update max X for join positioning
            if (branchNodeX + NODE_WIDTH > maxBranchX - NODE_SPACING_X) {
                maxBranchX = branchNodeX + NODE_WIDTH + NODE_SPACING_X;
            }
        }

        // Find branch start node (StartAt)
        const branchStartId = `${branchPrefix}-${branch.StartAt}`;

        // Connect fork to branch start with parallel edge type
        const forkEdgeData: WorkflowEdgeData = {
            edgeType: 'parallel',
        };
        edges.push({
            id: `${forkId}-branch-${branchIdx}->${branchStartId}`,
            source: forkId,
            sourceHandle: `branch-${branchIdx}`,
            target: branchStartId,
            targetHandle: 'target',
            type: 'workflow',
            data: forkEdgeData,
            markerEnd: { type: MarkerType.ArrowClosed, color: '#111827' },
        });

        // Find branch end nodes
        const branchEndNodes = findBranchEndNodes(branch, branchPrefix);

        // Connect branch end nodes to join
        branchEndNodes.forEach((endNodeId) => {
            const joinEdgeData: WorkflowEdgeData = {
                edgeType: 'parallel',
            };
            edges.push({
                id: `${endNodeId}->${joinId}-branch-${branchIdx}`,
                source: endNodeId,
                sourceHandle: 'source',
                target: joinId,
                targetHandle: `branch-${branchIdx}`,
                type: 'workflow',
                data: joinEdgeData,
                markerEnd: { type: MarkerType.ArrowClosed, color: '#111827' },
            });
        });
    });

    // Position join node after all branches, as child of container
    nodes.push({
        id: joinId,
        type: 'parallelJoin',
        position: { x: maxBranchX, y: position.y },
        data: joinData,
        // Set dimensions for layout calculation
        width: GATEWAY_NODE_WIDTH,
        height: GATEWAY_NODE_HEIGHT,
        parentId: containerId,
        extent: 'parent',
        expandParent: true,
        zIndex: 1,
    } as WorkflowNode);

    return { nodes, edges };
}

/**
 * Create the container structure for a Map state with nested iterator nodes.
 * 
 * The Map container visually groups the iterator states inside it,
 * using ReactFlow's parentNode feature for nested nodes.
 */
function createMapContainerStructure(
    nodeId: string,
    stateName: string,
    state: AslState,
    position: { x: number; y: number },
    workflowStartAt: string,
    parentId?: string,
    registry?: ResourceRegistry
): ConversionResult {
    const nodes: WorkflowNode[] = [];
    const edges: WorkflowEdge[] = [];
    const validationIssues: ValidationIssue[] = [];

    const iterator = state.Iterator;
    if (!iterator) {
        // Fallback: create simple Map node without iterator
        const nodeData: StateNodeData = {
            nodeType: 'state',
            label: stateName,
            stateType: 'Map',
            state,
            isStart: stateName === workflowStartAt && !parentId,
            isEnd: state.End === true && !parentId,
            badges: extractBadges(state),
        };
        nodes.push({
            id: nodeId,
            type: 'map',
            position,
            data: nodeData,
            // Set explicit dimensions for layout calculation before React Flow measures
            width: NODE_WIDTH,
            height: NODE_HEIGHT,
        });
        return { nodes, edges, validationIssues };
    }

    // Container configuration
    const CONTAINER_PADDING_LEFT = 120; // Extra padding for start marker
    const CONTAINER_PADDING_RIGHT = 180; // Extra padding for end marker  
    const CONTAINER_PADDING_TOP = 30; // Padding above first row
    const CONTAINER_PADDING_BOTTOM = 60; // Padding below last row
    const HEADER_HEIGHT = 52; // Height of container header
    const MIN_CONTAINER_WIDTH = 600;
    const MIN_CONTAINER_HEIGHT = 280;

    // First, convert iterator states to calculate dimensions
    const iteratorPrefix = `${nodeId}-iter`;
    const iteratorNodes: WorkflowNode[] = [];
    const iteratorEdges: WorkflowEdge[] = [];
    const iteratorPositions = new Map<string, { x: number; y: number }>();
    const iteratorVisited = new Set<string>();

    // Get iterator states with null check
    const iteratorStates = iterator.States || {};

    // Calculate positions for iterator states (relative to container)
    // Start positioning after header and left padding (leaving room for start marker)
    calculatePositions(
        iteratorStates,
        iterator.StartAt,
        iteratorPositions,
        iteratorVisited,
        CONTAINER_PADDING_LEFT,
        HEADER_HEIGHT + CONTAINER_PADDING_TOP
    );

    // Track bounds for container sizing
    let maxX = 0;
    let maxY = 0;

    // Track Parallel states within iterator for edge redirection
    const iteratorParallelStates = new Set<string>();
    for (const [iterStateName, iterState] of Object.entries(iteratorStates)) {
        if (iterState.Type === 'Parallel' && iterState.Branches && iterState.Branches.length > 0) {
            iteratorParallelStates.add(iterStateName);
        }
    }

    // Create iterator state nodes (as children of container)
    for (const [iterStateName, iterState] of Object.entries(iteratorStates)) {
        const iterNodeId = `${iteratorPrefix}-${iterStateName}`;
        const iterPosition = iteratorPositions.get(iterStateName) || { 
            x: CONTAINER_PADDING_LEFT, 
            y: HEADER_HEIGHT + CONTAINER_PADDING_TOP 
        };

        // Handle Parallel state inside iterator - create fork/join structure
        if (iterState.Type === 'Parallel' && iterState.Branches && iterState.Branches.length > 0) {
            const parallelResult = createParallelStructureAsChild(
                iterNodeId,
                iterStateName,
                iterState,
                iterPosition,
                nodeId, // Parent container ID
                iteratorPrefix,
                registry
            );
            iteratorNodes.push(...parallelResult.nodes);
            iteratorEdges.push(...parallelResult.edges);
            
            // Update bounds to account for parallel structure
            parallelResult.nodes.forEach((pNode) => {
                const pWidth = pNode.width ?? NODE_WIDTH;
                const pHeight = pNode.height ?? NODE_HEIGHT;
                maxX = Math.max(maxX, pNode.position.x + pWidth);
                maxY = Math.max(maxY, pNode.position.y + pHeight);
            });

            // Create Next edge from the join node if the Parallel has Next
            if (iterState.Next) {
                let targetId = `${iteratorPrefix}-${iterState.Next}`;
                // Redirect to fork if targeting another Parallel
                if (iteratorParallelStates.has(iterState.Next)) {
                    targetId = `${iteratorPrefix}-${iterState.Next}-fork`;
                }
                const nextEdgeData: WorkflowEdgeData = {
                    edgeType: 'normal',
                };
                iteratorEdges.push({
                    id: `${iterNodeId}-join->${targetId}`,
                    source: `${iterNodeId}-join`,
                    sourceHandle: 'source',
                    target: targetId,
                    targetHandle: 'target',
                    type: 'workflow',
                    data: nextEdgeData,
                    markerEnd: { type: MarkerType.ArrowClosed, color: '#374151' },
                });
            }
            
            continue;
        }

        // Update bounds using node dimensions
        maxX = Math.max(maxX, iterPosition.x + NODE_WIDTH);
        maxY = Math.max(maxY, iterPosition.y + NODE_HEIGHT);

        // Extract resource type for Task states (using registry for lookup)
        const resourceResult = iterState.Type === 'Task' ? extractResourceType(iterState, registry) : undefined;

        // Important: Child nodes should NOT set isStart/isEnd because:
        // - isStart would hide input handle (needed for start marker connection)
        // - isEnd would hide output handle (needed for end marker connection)
        // These flags are only for root-level workflow states
        const iterNodeData: StateNodeData = {
            nodeType: 'state',
            label: iterStateName,
            stateType: iterState.Type as AslStateType,
            state: iterState,
            isStart: false, // Always false - child needs input handle from start marker
            isEnd: false,   // Always false - child needs output handle to end marker or next state
            badges: extractBadges(iterState),
            ...(resourceResult?.type && { resourceType: resourceResult.type }),
            ...(resourceResult?.appKey && { resourceAppKey: resourceResult.appKey }),
            ...(resourceResult?.registryMatch != null && { resourceRegistryMatch: resourceResult.registryMatch }),
            ...(resourceResult?.error && { resourceError: resourceResult.error }),
        };

        iteratorNodes.push({
            id: iterNodeId,
            type: iterState.Type.toLowerCase(),
            position: iterPosition,
            data: iterNodeData,
            // Set explicit dimensions for layout calculation before React Flow measures
            width: NODE_WIDTH,
            height: NODE_HEIGHT,
            // Mark as child of the container (xyflow v12+ uses parentId)
            parentId: nodeId,
            extent: 'parent',
            expandParent: true,
            zIndex: 1, // Render above parent container
        } as WorkflowNode);

        // Create edges within iterator (with parallel state redirection)
        iteratorEdges.push(...createEdgesForState(
            iterNodeId, 
            iterStateName, 
            iterState, 
            iteratorPrefix,
            iteratorParallelStates
        ));
    }

    // Calculate container dimensions with proper padding for end markers
    const containerWidth = Math.max(MIN_CONTAINER_WIDTH, maxX + CONTAINER_PADDING_RIGHT);
    const containerHeight = Math.max(MIN_CONTAINER_HEIGHT, maxY + CONTAINER_PADDING_BOTTOM);

    // Create the Map container node
    const containerData: MapContainerNodeData = {
        nodeType: 'mapContainer',
        label: stateName,
        state,
        badges: extractBadges(state),
        itemsPath: state.ItemsPath,
        maxConcurrency: state.MaxConcurrency,
        containerWidth,
        containerHeight,
        iterator,
        isStart: stateName === workflowStartAt && !parentId,
        isEnd: state.End === true && !parentId,
    };

    nodes.push({
        id: nodeId,
        type: 'mapContainer',
        position,
        data: containerData,
        // CRITICAL: width/height must be set directly on node for extent: 'parent' to work
        // These properties are used by React Flow to calculate child bounds
        width: containerWidth,
        height: containerHeight,
        // Style is used for visual rendering
        style: { width: containerWidth, height: containerHeight },
        zIndex: 0,
    } as WorkflowNode);

    // Add iterator nodes and edges
    nodes.push(...iteratorNodes);
    edges.push(...iteratorEdges);

    // Add start marker for iterator (inside container)
    const iterStartMarkerId = `${iteratorPrefix}-__start__`;
    const iterStartPosition = iteratorPositions.get(iterator.StartAt) || { 
        x: CONTAINER_PADDING_LEFT, 
        y: HEADER_HEIGHT + CONTAINER_PADDING_TOP 
    };

    const iterStartMarkerData: StartMarkerNodeData = {
        nodeType: 'startMarker',
        label: 'Start',
        targetStateName: iterator.StartAt,
    };

    // Position start marker to the left of first state, but within container
    // Use fixed position near left edge of container
    nodes.push({
        id: iterStartMarkerId,
        type: 'startMarker',
        position: { x: 20, y: iterStartPosition.y },
        data: iterStartMarkerData,
        // Set dimensions for layout calculation
        width: START_MARKER_WIDTH,
        height: START_MARKER_HEIGHT,
        parentId: nodeId,
        extent: 'parent',
        expandParent: true,
        zIndex: 1,
    } as WorkflowNode);

    // Connect iterator start marker to first state
    const iterFirstStateId = `${iteratorPrefix}-${iterator.StartAt}`;
    const startEdgeData: WorkflowEdgeData = {
        edgeType: 'normal',
    };
    edges.push({
        id: `${iterStartMarkerId}->${iterFirstStateId}`,
        source: iterStartMarkerId,
        sourceHandle: 'source',
        target: iterFirstStateId,
        targetHandle: 'target',
        type: 'workflow',
        data: startEdgeData,
        markerEnd: { type: MarkerType.ArrowClosed, color: '#374151' },
    });

    return { nodes, edges, validationIssues };
}

/**
 * Find all end nodes in a branch (states with End=true or terminal types).
 */
function findBranchEndNodes(branch: AslDefinition, prefix: string): string[] {
    const endNodes: string[] = [];
    const branchStates = branch.States || {};

    for (const [stateName, state] of Object.entries(branchStates)) {
        if (
            state.End === true ||
            state.Type === 'Succeed' ||
            state.Type === 'Fail' ||
            (!state.Next && state.Type !== 'Choice')
        ) {
            endNodes.push(`${prefix}-${stateName}`);
        }
    }

    // If no explicit end nodes found, use all states without Next transitions
    // (excluding Choice which uses Choices/Default)
    if (endNodes.length === 0) {
        for (const [stateName, state] of Object.entries(branchStates)) {
            if (!state.Next && state.Type !== 'Choice') {
                endNodes.push(`${prefix}-${stateName}`);
            }
        }
    }

    return endNodes;
}

/**
 * Calculate positions for nodes using a simple layout algorithm.
 */
function calculatePositions(
    states: Record<string, AslState>,
    startAt: string,
    positions: Map<string, { x: number; y: number }>,
    visited: Set<string>,
    offsetX: number,
    offsetY: number
): void {
    // Guard against null/undefined states
    if (!states || typeof states !== 'object') {
        return;
    }
    
    const queue: Array<{ name: string; level: number; branch: number }> = [
        { name: startAt, level: 0, branch: 0 },
    ];

    const levelBranches = new Map<number, number>();

    while (queue.length > 0) {
        const { name, level, branch } = queue.shift()!;

        if (visited.has(name) || !states[name]) continue;
        visited.add(name);

        // Track branches at each level for horizontal spacing
        const currentBranch = levelBranches.get(level) || 0;
        levelBranches.set(level, currentBranch + 1);

        positions.set(name, {
            x: offsetX + level * NODE_SPACING_X,
            y: offsetY + branch * NODE_SPACING_Y,
        });

        const state = states[name];

        // Queue next states
        if (state.Next && !visited.has(state.Next)) {
            queue.push({ name: state.Next, level: level + 1, branch });
        }

        // Handle Choice branches
        if (state.Type === 'Choice') {
            state.Choices?.forEach((choice, idx) => {
                if (choice.Next && !visited.has(choice.Next)) {
                    queue.push({ name: choice.Next, level: level + 1, branch: branch + idx });
                }
            });
            if (state.Default && !visited.has(state.Default)) {
                queue.push({
                    name: state.Default,
                    level: level + 1,
                    branch: branch + (state.Choices?.length || 0),
                });
            }
        }
    }
}

/**
 * Create edges for a state based on its transitions.
 * Redirects edges to Parallel states to their fork nodes.
 */
function createEdgesForState(
    nodeId: string,
    stateName: string,
    state: AslState,
    parentId?: string,
    parallelStates: Set<string> = new Set()
): WorkflowEdge[] {
    const edges: WorkflowEdge[] = [];
    const targetPrefix = parentId ? `${parentId}-` : '';

    /**
     * Get the actual target ID, redirecting to fork for Parallel states.
     */
    function getTargetId(targetName: string): string {
        const baseTargetId = `${targetPrefix}${targetName}`;
        // If targeting a Parallel state, redirect to its fork node
        if (parallelStates.has(targetName)) {
            return `${baseTargetId}-fork`;
        }
        return baseTargetId;
    }

    // Dynamic Next transition (JSONPath-resolved at runtime)
    const isDynamicNext = state.Next && (state.Next.startsWith('$.') || state.Next.startsWith('$$.'));
    if (isDynamicNext && state.NextTargets && state.NextTargets.length > 0) {
        state.NextTargets.forEach((target, idx) => {
            const targetId = getTargetId(target);
            const dynamicEdgeData: WorkflowEdgeData = {
                edgeType: 'dynamic',
                label: target,
                isDynamic: true,
            };
            edges.push({
                id: `${nodeId}-dynamic-${idx}->${targetId}`,
                source: nodeId,
                sourceHandle: 'source',
                target: targetId,
                targetHandle: 'target',
                type: 'workflow',
                data: dynamicEdgeData,
                markerEnd: { type: MarkerType.ArrowClosed, color: '#0891b2' },
            });
        });
    } else if (state.Next && !isDynamicNext) {
        // Standard Next transition
        const targetId = getTargetId(state.Next);
        const nextEdgeData: WorkflowEdgeData = {
            edgeType: 'normal',
        };
        edges.push({
            id: `${nodeId}->${targetId}`,
            source: nodeId,
            sourceHandle: 'source',
            target: targetId,
            targetHandle: 'target',
            type: 'workflow',
            data: nextEdgeData,
            markerEnd: { type: MarkerType.ArrowClosed, color: '#374151' },
        });
    }

    // Choice state branches
    // All choice edges originate from the same source handle on ChoiceNode
    if (state.Type === 'Choice') {
        state.Choices?.forEach((choice, idx) => {
            if (choice.Next) {
                const targetId = getTargetId(choice.Next);
                const choiceEdgeData: WorkflowEdgeData = {
                    edgeType: 'choice',
                    label: getChoiceLabel(choice, idx),
                    condition: choice,
                };
                edges.push({
                    id: `${nodeId}-choice-${idx}->${targetId}`,
                    source: nodeId,
                    sourceHandle: 'source',
                    target: targetId,
                    targetHandle: 'target',
                    type: 'workflow',
                    data: choiceEdgeData,
                    markerEnd: { type: MarkerType.ArrowClosed, color: '#1e40af' },
                });
            }
        });

        if (state.Default) {
            const targetId = getTargetId(state.Default);
            const defaultEdgeData: WorkflowEdgeData = {
                edgeType: 'default',
                label: 'default',
                isDefault: true,
            };
            edges.push({
                id: `${nodeId}-default->${targetId}`,
                source: nodeId,
                sourceHandle: 'source',
                target: targetId,
                targetHandle: 'target',
                type: 'workflow',
                data: defaultEdgeData,
                markerEnd: { type: MarkerType.ArrowClosed, color: '#1e40af' },
            });
        }
    }

    // Catch transitions (error handling)
    state.Catch?.forEach((catchConfig, idx) => {
        const targetId = getTargetId(catchConfig.Next);
        const catchLabel = catchConfig.ErrorEquals.includes('States.ALL')
            ? 'catch all'
            : catchConfig.ErrorEquals.join(', ');
        const catchEdgeData: WorkflowEdgeData = {
            edgeType: 'catch',
            label: catchLabel,
            errorEquals: catchConfig.ErrorEquals,
        };
        edges.push({
            id: `${nodeId}-catch-${idx}->${targetId}`,
            source: nodeId,
            sourceHandle: 'source',
            target: targetId,
            targetHandle: 'target',
            type: 'workflow',
            data: catchEdgeData,
            markerEnd: { type: MarkerType.ArrowClosed, color: '#ef4444' },
        });
    });

    return edges;
}

/**
 * Generate a human-readable label for a choice condition.
 */
function getChoiceLabel(choice: AslChoice, index: number): string {
    const operators = [
        'StringEquals',
        'NumericEquals',
        'NumericGreaterThan',
        'NumericLessThan',
        'BooleanEquals',
        'IsNull',
        'IsPresent',
    ];

    for (const op of operators) {
        if (op in choice) {
            const variable = choice.Variable?.replace('$.', '') || 'value';
            const value = (choice as unknown as Record<string, unknown>)[op];
            return `${variable} ${formatOperator(op)} ${value}`;
        }
    }

    // Handle compound operators
    if (choice.And) {
        return `AND (${choice.And.length} conditions)`;
    }
    if (choice.Or) {
        return `OR (${choice.Or.length} conditions)`;
    }
    if (choice.Not) {
        return `NOT (...)`;
    }

    return `choice ${index + 1}`;
}

/**
 * Format an operator for human-readable display.
 */
function formatOperator(op: string): string {
    const formatMap: Record<string, string> = {
        StringEquals: '==',
        NumericEquals: '==',
        NumericGreaterThan: '>',
        NumericLessThan: '<',
        BooleanEquals: '==',
        IsNull: 'is null',
        IsPresent: 'is present',
        StringGreaterThan: '>',
        StringLessThan: '<',
    };
    return formatMap[op] || op;
}

// NODE_WIDTH and NODE_HEIGHT are exported at declaration
// NODE_SPACING_X and NODE_SPACING_Y are internal only
